import { Fragment, HTMLAttributes, MouseEventHandler, forwardRef, useState } from 'react';
import { Button } from '../button/button';
import { FileWithPreview, UploadedFile, UploadingFile } from './types';
import { DragAndDrop, DragAndDropProps } from './drag-and-drop/drag-and-drop';
import { FileUploadProgress } from './file-upload-progress/file-upload-progress';
import { FileUploadedChip } from './file-uploaded-chip/file-uploaded-chip';
import { FileUploadedPreview } from './file-uploaded-preview/file-uploaded-preview';
import './upload-file.css';
import { useDevice, useWindowSize } from '../hooks';
import { FieldErrorMessage } from '../../forms/core';

interface UploadFileProps
  extends HTMLAttributes<HTMLDivElement>,
    DragAndDropProps {
  uploadedFiles: Record<string, UploadedFile>;
  uploadingFiles: Record<string, UploadingFile>;
  name: string;
  disabled?: boolean;
  hasError?: boolean;
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  onDeleteUploadedFile?: (
    fileName: string,
  ) => MouseEventHandler<HTMLButtonElement>;
  onCancelUploadingFile?: (
    fileName: string,
  ) => MouseEventHandler<HTMLButtonElement>;
  onDeleteUploadingFile?: (
    fileName: string,
  ) => MouseEventHandler<HTMLButtonElement>;
  onDeleteAllUploadingFiles?: MouseEventHandler<HTMLButtonElement>;
}

const UploadFile = forwardRef<HTMLDivElement, UploadFileProps>(
  (
    {
      uploadedFiles,
      onDeleteUploadedFile,
      onAcceptedFiles,
      accept,
      maxFiles,
      maxSize,
      includePreview,
      uploadingFiles,
      onCancelUploadingFile,
      onDeleteUploadingFile,
      onDeleteAllUploadingFiles,
      className,
      name,
      onInputChange,
      disabled,
      hasError,
      ...props
    },
    ref,
  ) => {
    const defaultMaxFiles = maxFiles ?? 1;

    const filesUploaded = Object.keys(uploadedFiles);
    const filesUploading = Object.keys(uploadingFiles);

    const dimensions = useWindowSize();
    const device = useDevice(dimensions);

    const [validationError, setValidationError] = useState('');

    const handleAcceptedFiles = (files: FileWithPreview[]) => {
      if (filesUploaded.length + filesUploading.length + files.length > defaultMaxFiles) {
        setValidationError(`The maximum number of files allowed is ${defaultMaxFiles}`);
        return;
      }

      setValidationError('');
      onAcceptedFiles?.(files);
    };

    return (
      <>
        <div className={`upload-file ${className ?? ''}`} ref={ref} {...props}>
          {filesUploaded.length > 0 && (
            // File Uploaded Chips
            <div className="uf-uploaded">
              {filesUploaded.map(fileName => (
                <Fragment key={fileName}>
                  {uploadedFiles[fileName].preview && device === 'desktop' && (
                    <FileUploadedPreview
                      src={uploadedFiles[fileName].preview}
                      alt={`${fileName} preview`}
                    />
                  )}
                  <FileUploadedChip
                    file={uploadedFiles[fileName]}
                    onDelete={
                      disabled ? undefined : onDeleteUploadedFile?.(fileName)
                    }
                  />
                  {uploadedFiles[fileName].preview && device === 'mobile' && (
                    <FileUploadedPreview
                      src={uploadedFiles[fileName].preview}
                      alt={`${fileName} preview`}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          )}
          {filesUploading.length === 0 ? (
            // Drag & Drop
            filesUploaded.length < defaultMaxFiles && (
              <DragAndDrop
                name={name}
                onInputChange={onInputChange}
                onAcceptedFiles={handleAcceptedFiles}
                accept={accept}
                maxFiles={defaultMaxFiles}
                maxSize={maxSize}
                includePreview={includePreview}
                disabled={disabled}
                hasError={hasError}
              />
            )
          ) : (
            // Uploading
            <div className="uf-uploading">
              <div className="uf-info">
                <span className="uf-label">Uploading...</span>
                {defaultMaxFiles > 1 && (
                  <Button
                    onClick={onDeleteAllUploadingFiles}
                    variant="tertiary"
                    kind="danger"
                    size="small"
                    className="uf-delete-button"
                    disabled={disabled}
                  >
                    Cancel All
                  </Button>
                )}
              </div>
              <div className="uf-list">
                {filesUploading.map(fileName => (
                  <FileUploadProgress
                    key={fileName}
                    file={uploadingFiles[fileName]}
                    onCancel={
                      disabled ? undefined : onCancelUploadingFile?.(fileName)
                    }
                    onDelete={
                      disabled ? undefined : onDeleteUploadingFile?.(fileName)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {validationError && (
          <FieldErrorMessage message={validationError} />
        )}
      </>
    );
  },
);

export { UploadFile };
